import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { EVINCED_SEVERITIES, FormatHelper } from '@evinced-private/ui-common';

import {
	BaseScanViewV2,
	ScanTableDefinition
} from '../../../../components/scan-analysis/base/BaseScanViewV2';
import { MAX_RESULTS } from '../../../../consts/Results';
import { buildDeduplicatedSeveritySummary } from '../../../../helpers/SeverityLabelHelper';
import { useGetProperty } from '../../../../hooks/queries/useGetProperty';
import { IIssueTypesPaginationResponse } from '../../../../interfaces/IssueTypesPaginationResponse';
import { SummaryItem } from '../../../../interfaces/SummaryItem';
import { getIssueTypesSummary, getScanIssueTypes } from '../../../../services/scan/ScanService';
import { FilterParams } from '../../../../types/FilterParams';
import { IssueTypesSummary } from '../../../../types/OverviewTypes';
import { PaginationParams } from '../../../../types/PaginationParams';

import { ScanIssueTypesTableHelperV2 } from './ScanIssueTypesTableHelperV2';

interface IScanIssueTypesView {
	scanId: string;
	propertyId: string;
	viewId?: string;
}

const defaultTableSort = ScanIssueTypesTableHelperV2.getDefaultSort();

const ScanIssueTypesView: FC<IScanIssueTypesView> = ({
	scanId,
	propertyId,
	viewId
}: IScanIssueTypesView) => {
	const [scanResults, setScanResults] = useState<IIssueTypesPaginationResponse>(null);
	const [totalCount, setTotalCount] = useState<number>(null);
	const [summaryPaneInfo, setSummaryPaneInfo] = useState<IssueTypesSummary>();
	const screenId = `scan-${scanId}-view-${viewId}-issues-view`;
	const { data: property } = useGetProperty(propertyId);
	const customSeverityNames = useMemo(
		() => property?.scanConfiguration?.customSeverityNames ?? {},
		[property]
	);

	const loadTableItems = useCallback(
		async (
			scanId: string,
			paginationParams: PaginationParams,
			filtersParams?: FilterParams
		): Promise<void> => {
			const response: IIssueTypesPaginationResponse = await getScanIssueTypes(
				scanId,
				paginationParams,
				filtersParams,
				viewId
			);
			setScanResults(response);
		},
		[viewId]
	);

	useEffect(() => {
		if (scanResults?.overallTotal) {
			setTotalCount(scanResults.overallTotal);
		}
	}, [scanResults?.overallTotal]);

	const loadSummaryData = useCallback(async (): Promise<void> => {
		const summaryPaneInfo: IssueTypesSummary = await getIssueTypesSummary(
			scanId,
			viewId,
			customSeverityNames
		);
		setSummaryPaneInfo(summaryPaneInfo);
	}, [scanId, viewId, customSeverityNames]);

	const severityEntries = useMemo(
		() =>
			buildDeduplicatedSeveritySummary(
				summaryPaneInfo?.severities,
				[EVINCED_SEVERITIES.CRITICAL, EVINCED_SEVERITIES.SERIOUS],
				customSeverityNames
			),
		[summaryPaneInfo?.severities, customSeverityNames]
	);

	const summaryPaneData = useMemo((): SummaryItem[] => {
		if (!summaryPaneInfo) {
			return [];
		}
		const { totalNumberOfIssues } = summaryPaneInfo;
		return [
			{
				value: FormatHelper.formatNumber(totalNumberOfIssues),
				label: 'Total issues'
			},
			...severityEntries.map((entry) => ({
				value: FormatHelper.formatNumber(entry.value),
				label: `${entry.label} issues`
			}))
		];
	}, [summaryPaneInfo, severityEntries]);

	const tableDefinition = useMemo((): ScanTableDefinition => {
		if (!scanResults) {
			return null;
		}

		const { types } = scanResults;
		const totalPaginationableResults =
			scanResults.overallTotal > MAX_RESULTS ? MAX_RESULTS : scanResults.overallTotal;

		return ScanIssueTypesTableHelperV2.getTableDefinition(
			types,
			propertyId,
			scanId,
			totalCount,
			totalPaginationableResults,
			screenId
		);
	}, [scanResults, totalCount, propertyId, scanId, screenId]);

	return (
		<BaseScanViewV2
			viewId={viewId}
			scanId={scanId}
			screenId={screenId}
			summaryPaneItems={summaryPaneData}
			tableDefinition={tableDefinition}
			defaultTableSort={defaultTableSort}
			loadSummaryData={loadSummaryData}
			loadTableItems={loadTableItems}
			propertyId={propertyId}
		/>
	);
};

export { ScanIssueTypesView };
